import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  Download, 
  Calendar, 
  Filter, 
  RefreshCw,
  FileText,
  Users,
  Building,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  ArrowUpCircle
} from 'lucide-react';
import reportService from '@/services/reportService';
import { downloadComplaintReportExcel } from '@/services/exportService';
import { ComplaintReport, ReportFilters } from '@/types';

const ComplaintReportsPage: React.FC = () => {
  const [reportData, setReportData] = useState<ComplaintReport | null>(null);
  const [complaintsWithoutUpdates, setComplaintsWithoutUpdates] = useState<any>(null);
  const [escalatedComplaints, setEscalatedComplaints] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ReportFilters>({
    date_from: reportService.getDateRange('month').from,
    date_to: reportService.getDateRange('month').to
  });
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchAllReportData();
  }, [filters]);

  const fetchAllReportData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [reportDataResult, noUpdatesResult, escalatedResult] = await Promise.all([
        reportService.getComplaintReport(filters),
        reportService.getComplaintsWithoutUpdates().catch(() => ({ total: 0, complaints: [] })),
        reportService.getEscalatedComplaints().catch(() => ({ data: [] }))
      ]);
      
      setReportData(reportDataResult);
      setComplaintsWithoutUpdates(noUpdatesResult);
      setEscalatedComplaints(escalatedResult);
    } catch (error) {
      console.error('Error fetching complaint report:', error);
      setError('Failed to load complaint report');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (key: keyof ReportFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined
    }));
  };

  const handleExport = async (format: 'json' | 'csv' | 'xlsx') => {
    setIsExporting(true);
    try {
      if (format === 'xlsx') {
        await downloadComplaintReportExcel(filters as Record<string, string | undefined>);
      } else {
        await reportService.downloadReport('complaints', format, filters);
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      setError('Failed to export report');
    } finally {
      setIsExporting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'assigned': return <Users className="h-5 w-5 text-blue-500" />;
      case 'in_progress': return <RefreshCw className="h-5 w-5 text-purple-500" />;
      case 'on_hold': return <AlertCircle className="h-5 w-5 text-orange-500" />;
      case 'resolved': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'closed': return <XCircle className="h-5 w-5 text-gray-500" />;
      default: return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'urgent': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Report</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchAllReportData}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Data Available</h2>
          <p className="text-gray-600">No complaint data found for the selected period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Complaint Reports</h1>
              <p className="mt-2 text-gray-600">
                Detailed analysis of complaint data and trends
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={fetchAllReportData}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleExport('xlsx')}
                  disabled={isExporting}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Excel
                </button>
                <button
                  onClick={() => handleExport('csv')}
                  disabled={isExporting}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </button>
                <button
                  onClick={() => handleExport('json')}
                  disabled={isExporting}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export JSON
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <div className="flex items-center mb-4">
            <Filter className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Filters</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={filters.date_from || ''}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={filters.date_to || ''}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="assigned">Assigned</option>
                <option value="in_progress">In Progress</option>
                <option value="on_hold">On Hold</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={filters.priority || ''}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="h-6 w-6 text-blue-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Complaints</dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      {reportData.total_complaints}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Resolved</dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      {reportData.resolved_complaints}
                    </dd>
                    <dd className="text-xs text-gray-500 mt-1">
                      {reportData.total_complaints > 0 
                        ? `${((reportData.resolved_complaints / reportData.total_complaints) * 100).toFixed(1)}% resolution rate`
                        : '0% resolution rate'}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <RefreshCw className="h-6 w-6 text-purple-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">In Progress</dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      {(reportData as any).status_breakdown?.in_progress || reportData.complaints_by_status?.in_progress || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BarChart3 className="h-6 w-6 text-indigo-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Avg Resolution</dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      {reportData.average_resolution_time} days
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status Breakdown Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          {Object.entries((reportData as any).status_breakdown || reportData.complaints_by_status || {}).map(([status, count]: [string, any]) => (
            <div key={status} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {getStatusIcon(status)}
                    <span className="ml-2 text-xs font-medium text-gray-700 capitalize">
                      {status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                <div className="mt-2">
                  <div className="text-2xl font-bold text-gray-900">{count}</div>
                  <div className="text-xs text-gray-500">
                    {reportData.total_complaints > 0 
                      ? `${((count / reportData.total_complaints) * 100).toFixed(1)}%`
                      : '0%'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Alerts Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Complaints Without Updates */}
          <div className="bg-white shadow rounded-lg p-6 border-l-4 border-orange-500">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <AlertTriangle className="h-6 w-6 text-orange-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">No Updates (24h+)</h3>
              </div>
              <span className="text-2xl font-bold text-orange-600">
                {complaintsWithoutUpdates?.total || 0}
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Complaints with no status change or update in the last 24 hours
            </p>
            {complaintsWithoutUpdates?.complaints && complaintsWithoutUpdates.complaints.length > 0 && (
              <div className="max-h-48 overflow-y-auto space-y-2">
                {complaintsWithoutUpdates.complaints.slice(0, 5).map((complaint: any, index: number) => (
                  <div key={index} className="text-sm p-2 bg-gray-50 rounded">
                    <div className="font-medium text-gray-900">{complaint.complaint_number || complaint.title}</div>
                    <div className="text-xs text-gray-500">
                      {complaint.department_name || 'Unknown'} • {complaint.hours_since_update}h ago
                    </div>
                  </div>
                ))}
                {complaintsWithoutUpdates.complaints.length > 5 && (
                  <div className="text-xs text-gray-500 text-center pt-2">
                    +{complaintsWithoutUpdates.complaints.length - 5} more
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Escalated Complaints */}
          <div className="bg-white shadow rounded-lg p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <ArrowUpCircle className="h-6 w-6 text-red-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Escalated</h3>
              </div>
              <span className="text-2xl font-bold text-red-600">
                {Array.isArray(escalatedComplaints?.data) ? escalatedComplaints.data.length : (escalatedComplaints?.pagination?.total || 0)}
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Complaints that have been escalated for management attention
            </p>
            {escalatedComplaints?.data && escalatedComplaints.data.length > 0 && (
              <div className="max-h-48 overflow-y-auto space-y-2">
                {escalatedComplaints.data.slice(0, 5).map((complaint: any, index: number) => (
                  <div key={index} className="text-sm p-2 bg-gray-50 rounded">
                    <div className="font-medium text-gray-900">{complaint.complaint_number || complaint.title}</div>
                    <div className="text-xs text-gray-500">
                      {complaint.department_name || 'Unknown'} • {complaint.priority}
                    </div>
                  </div>
                ))}
                {escalatedComplaints.data.length > 5 && (
                  <div className="text-xs text-gray-500 text-center pt-2">
                    +{escalatedComplaints.data.length - 5} more
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Charts and Analysis */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Status Distribution */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Complaints by Status</h3>
            <div className="space-y-4">
              {Object.entries(reportData.complaints_by_status).map(([status, count]) => {
                const percentage = (count / reportData.total_complaints) * 100;
                return (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center">
                      {getStatusIcon(status)}
                      <span className="ml-2 text-sm font-medium text-gray-900 capitalize">
                        {status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-12 text-right">
                        {count} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Priority Distribution */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Complaints by Priority</h3>
            <div className="space-y-4">
              {Object.entries(reportData.complaints_by_priority).map(([priority, count]) => {
                const percentage = (count / reportData.total_complaints) * 100;
                return (
                  <div key={priority} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(priority)}`}>
                        {priority}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-12 text-right">
                        {count} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Department Performance */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Department Performance</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Complaints
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Percentage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.complaints_by_department.map((dept, index) => {
                  const percentage = (dept.count / reportData.total_complaints) * 100;
                  const maxCount = Math.max(...reportData.complaints_by_department.map(d => d.count));
                  const performancePercentage = (dept.count / maxCount) * 100;
                  
                  return (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {dept.department_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {dept.count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {percentage.toFixed(1)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ width: `${performancePercentage}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-500">
                            {performancePercentage.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Monthly Trends */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Monthly Trends</h3>
          <div className="space-y-4">
            {reportData.complaints_by_month.map((month, index) => {
              const maxCount = Math.max(...reportData.complaints_by_month.map(m => m.count));
              const percentage = (month.count / maxCount) * 100;
              
              return (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">{month.month}</span>
                  <div className="flex items-center">
                    <div className="w-64 bg-gray-200 rounded-full h-3 mr-3">
                      <div 
                        className="bg-blue-600 h-3 rounded-full" 
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-12 text-right">
                      {month.count}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplaintReportsPage;
